import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { analytics } from '../utils/analytics';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (user, token) => {
        set({ 
          user, 
          token,
          isAuthenticated: true,
          error: null 
        });

        // Track login event
        analytics.trackEvent({
          category: 'Auth',
          action: 'login',
          label: user.email,
        });
      },

      logout: () => {
        // Track logout event before clearing user data
        const user = useAuthStore.getState().user;
        if (user) {
          analytics.trackEvent({
            category: 'Auth',
            action: 'logout',
            label: user.email,
          });
        }

        set({ 
          user: null, 
          token: null,
          isAuthenticated: false,
          error: null 
        });
      },

      updateUser: (updates) => {
        set((state) => {
          if (!state.user) return state;

          const updatedUser = { ...state.user, ...updates };

          // Track user update
          analytics.trackEvent({
            category: 'User',
            action: 'update',
            label: state.user.email,
            metadata: updates,
          });

          return { user: updatedUser };
        });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Add auth state change listener
useAuthStore.subscribe((state) => {
  // Update document title based on auth state
  document.title = state.isAuthenticated 
    ? `توازن - ${state.user?.name}`
    : 'توازن - منصة الأهداف الشخصية';

  // Update HTML dir attribute based on user preferences
  if (state.user?.preferences?.language) {
    document.documentElement.dir = state.user.preferences.language === 'ar' ? 'rtl' : 'ltr';
  }
});